import { Overlay } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  Injector,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { isDefined } from '@sama/js-core';
import { flatten } from '@sama/micro-dash';
import { DirectiveSuperclass } from '@sama/ng-core';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { BrowserControlService } from '../../../../cross-team-shared/misc-services/browser-control.service';
import { Project } from '../../../../team-apis/otterdam/project';
import { Step } from '../../../../team-apis/otterdam/step';
import { Submission } from '../../../../team-apis/otterdam/submission';
import { Task } from '../../../../team-apis/otterdam/task';
import { TaskWorkspaceStore } from '../../../h3ml/state/task-workspace-store';
import { LinkedRadioButtonDropdownItem } from '../../../shared/navbar/linked-radio-button-dropdown/linked-radio-button-dropdown.component';
import { buildStepItem } from '../../../shared/navbar/steps-navigation/steps-navigation-helper';
import { CompareAnswersComponent } from '../../compare-answers/compare-answers.component';

export interface SubmissionDropdownItem extends LinkedRadioButtonDropdownItem {
  id?: number;
  name?: string;
}

@Component({
  selector: 'h3-submissions-navigation',
  templateUrl: './submissions-navigation.component.html',
  styleUrls: ['./submissions-navigation.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SubmissionsNavigationComponent
  extends DirectiveSuperclass
  implements AfterViewInit
{
  @Input({ required: true }) submissions!: Submission[];
  @ViewChild('rejected') rejectedTemplate!: TemplateRef<unknown>;
  submissionItems$!: Observable<SubmissionDropdownItem[]>;
  overlayIsOpen$ = new BehaviorSubject<boolean>(false);
  canCompareAnswers$ = this.store
    .isUsingGenAi$()
    .pipe(map((isUsingGenAi) => !isUsingGenAi));

  private browserControlService = inject(BrowserControlService);

  constructor(
    private store: TaskWorkspaceStore,
    private overlay: Overlay,
    private injector: Injector,
  ) {
    super(injector);
  }

  ngAfterViewInit() {
    this.submissionItems$ = combineLatest([
      this.store('project').$,
      this.store('task').$,
    ]).pipe(
      filter(([project, task]) => isDefined(project) && isDefined(task)),
      map(([project, task]) => this.buildSubmissionList(task, project)),
    );
  }

  openOverlay() {
    const positionStrategy = this.overlay
      .position()
      .global()
      .centerHorizontally()
      .top('70px');
    const overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
    });
    const componentRef = overlayRef.attach(
      new ComponentPortal(CompareAnswersComponent, null, this.injector),
    );

    // Not the prettiest code but I didn't find a nice way to set the pointer
    // events to none or dynamicly change the color of the backdrop
    // https://github.com/angular/components/issues/14062
    overlayRef.backdropElement!.style.opacity = '0';
    overlayRef.backdropElement!.style.pointerEvents = 'none';
    componentRef.instance.submissionItems$ = this.submissionItems$;
    componentRef.instance.submissionId = this.getSubmissionId();
    this.subscribeTo(componentRef.instance.closeOverlay, () => {
      overlayRef.detach();
      this.overlayIsOpen$.next(false);
    });

    this.subscribeTo(componentRef.instance.isLoading, (isLoading) => {
      overlayRef.backdropElement!.style.opacity = isLoading ? '1' : '0';
    });
    this.overlayIsOpen$.next(true);
  }

  private buildSubmissionItem(step: Step, submission: Submission) {
    return {
      label: `Step ${step.ordinal_letter} (round ${submission.round}) - ${step.step_type}`,
      active: submission.id === this.getSubmissionId(),
      url: `?submission_id=${submission.id}`,
      extraContent: submission.rejected ? this.rejectedTemplate : undefined,
      name: `${step.ordinal_letter}${submission.round}`,
      id: submission.id,
    };
  }

  private buildSubmissionList(_task: Task, project: Project) {
    return flatten(
      project.steps.map((step) => {
        const submissions = this.submissions.filter(
          // a bug in sh3 passes empty submissions when there is no
          // submissions, that's why we check the id
          (submission) => submission.step_id === step.id && !!submission.id,
        );

        if (!submissions.length) {
          return [buildStepItem(step)];
        }

        return submissions.map((submission) =>
          this.buildSubmissionItem(step, submission),
        );
      }),
    );
  }

  private getSubmissionId() {
    const params = this.browserControlService.getQueryParameters();
    const id = new URLSearchParams(params).get('submission_id');
    return id ? +id : undefined;
  }
}
